<script setup lang="ts">
	import {msg} from "@/i18n/Message";
	import {ref, computed} from 'vue';
	import ErrorMsg from "./ErrorMsg.vue";
	import { useBaseStep } from './BaseStep';
	import { useCrmInstallData } from '../CrmInstallData';
	import SymIcon from "@fd/helpers/SymIcon.vue";
	import ProgressButton from "./ProgressButton.vue";
	
	const emit = defineEmits(['nextStep','updateForm']);
	const props = defineProps({
		form: Object,
		restApi: String,
		endPoint: { type: String, default: "/application" }
	});
	const { errorMessage, isError, progress, submitOnEnter,
			sendForm, nextStep, errEmptyStringClass } = useBaseStep(props,emit,validateForm);
	const { getArrayData   } = useCrmInstallData();
			
	//default values
	const countries = ref([]);
	const languages = ref([]);
	const timeZones = ref([]);
	
	function validateForm(): boolean {
		if (!Boolean(props.form.appCompany)) {
			errorMessage.value=msg("install.form.errorValues");
			return false;
		}
		if (!Boolean(props.form.appStreet)) {
			errorMessage.value=msg("install.form.errorValues");
			return false;
		}
		if (!Boolean(props.form.appZip)) {
			errorMessage.value=msg("install.form.errorValues");
			return false;
		}
		if (!Boolean(props.form.appCity)) {
			errorMessage.value=msg("install.form.errorValues");
			return false;
		}
		if (!Boolean(props.form.appCountry)) {
			errorMessage.value=msg("install.form.errorValues");
			return false;
		}
		if (!Boolean(props.form.currency)) {
			errorMessage.value=msg("install.form.errorValues");
			return false;
		}
		return true;
	}
	
	function onSubmit(): void {
		sendForm(props.form,'User');
	}
	
	getArrayData(props.restApi+"/countries",[{key:"CZ",title:"Česká republika"}])
			.then((data)=>{countries.value=data});
	if (!Boolean(props.form.appCountry)) emit('updateForm', 'appCountry', "CZ");
	
	getArrayData(props.restApi+"/languages",[{key:"cs",title:"čeština"}])
			.then((data)=>{languages.value=data});
	if (!Boolean(props.form.langID)) emit('updateForm', 'langID', "cs");
		
	getArrayData(props.restApi+"/timeZones",[{key:"Europe/Prague",title:"EU-Česko"}])
			.then((data)=>{timeZones.value=data});
	if (!Boolean(props.form.timeZoneID)) emit('updateForm', 'timeZoneID', "Europe/Prague");
</script>
<template>
	<div>
	<div class="formTitle">{{msg("install.title.application")}}</div>
	<form @submit.prevent>
	<table border="0" cellspacing="0" class="formTable">
		<tr>
			<th>{{msg("install.app.company")}}:</th>
			<td><input type="text" :value="form.appCompany" size="30" maxlength="48" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appCompany', $event.target.value)" :class="errEmptyStringClass(form.appCompany)"></td>
		</tr>
		<tr>
			<th>{{msg("install.app.address")}}:</th>
			<td><input type="text" :value="form.appStreet" size="30" maxlength="80" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appStreet', $event.target.value)" :class="errEmptyStringClass(form.appStreet)"></td>
		</tr>
		<tr>
			<th>{{msg("install.app.zip")}}:</th>
			<td style="white-space: nowrap;"><input type="text" :value="form.appZip" maxlength="13" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appZip', $event.target.value)" :class="errEmptyStringClass(form.appZip)" style="max-width: 80px;">
				&nbsp;<span class="boldCell">{{msg("install.app.city")}}:</span>&nbsp;
				<input type="text" :value="form.appCity" size="20" maxlength="32" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appCity', $event.target.value)" :class="errEmptyStringClass(form.appCity)" style="max-width: 180px;"></td>
		</tr>
		<tr>
			<th>{{msg("install.app.country")}}:</th>
			<td>
				<select :value="form.appCountry" @input="$emit('updateForm', 'appCountry', $event.target.value)">
					<option v-for='country in countries' :value="country.key">{{country.title}}</option>
				</select>
				&nbsp;<span class="boldCell">{{msg("install.app.currency")}}:</span>
				&nbsp;<select :value="form.currency" @input="$emit('updateForm', 'currency', $event.target.value)">
					<option value="CZK">CZK</option>
					<option value="EUR">EUR</option>
					<option value="PLN">PLN</option>
					<option value="SKK">SKK</option>
					<option value="USD">USD</option>
					<option value="HRK">HRK</option>
					<option value="HUF">HUF</option>
				</select>
			</td>
		</tr>
		<tr>
			<th>{{msg("install.app.tel")}}:</th>
			<td><input type="text" :value="form.appTel" size="30" maxlength="64" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appTel', $event.target.value)"></td>
		</tr>
		<tr>
			<th>{{msg("install.app.mail")}}:</th>
			<td><input type="text" :value="form.appMail" size="30" maxlength="64" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appMail', $event.target.value)"></td>
		</tr>
		<tr>
			<td>{{msg("install.app.web")}}:</td>
			<td><input type="text" :value="form.appWeb" size="30" maxlength="48" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appWeb', $event.target.value)"></td>
		</tr>
		<tr>
			<td>{{msg("install.app.ico")}}:</td>
			<td><input type="text" :value="form.appIco" size="14" maxlength="10" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appIco', $event.target.value)"></td>
		</tr>
		<tr>
			<td>{{msg("install.app.dic")}}:</td>
			<td><input type="text" :value="form.appDic" size="18" maxlength="14" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appDic', $event.target.value)"></td>
		</tr>
		<tr>
			<td>{{msg("install.app.account")}}:</td>
			<td><input type="text" :value="form.appAccount" size="30" maxlength="48" @keypress="submitOnEnter($event,onSubmit)"
					@input="$emit('updateForm', 'appAccount', $event.target.value)"></td>
		</tr>
		<tr>
			<td>{{msg("install.app.lang")}}:</td>
			<td>
				<select :value="form.langID" style="width:200px;"
					@input="$emit('updateForm', 'langID', $event.target.value)">
					<option v-for='language in languages' :value="language.key">{{language.title}}</option>
				</select>
			</td>
		</tr>
		<tr>
			<td>{{msg("install.app.timezone")}}:</td>
			<td>
				<select :value="form.timeZoneID" style="max-width:320px"
					@input="$emit('updateForm', 'timeZoneID', $event.target.value)">
					<option v-for='timeZone in timeZones' :value="timeZone.key">{{timeZone.title}}</option>
				</select>
			</td>
		</tr>
		<tr>
			<td colspan="2"><error-msg :show="isError" :msg="errorMessage"/></td>
		</tr>
		<tr>
			<td colspan="2" style="padding-top:20px">
				<button @click="nextStep('Folders')" class="sbutton">
					<sym-icon name="page-prev-16-full" style="padding-right: 10px;"/>{{msg("install.butt.back")}}
				</button>
				&nbsp;
				<progress-button @click="onSubmit" :progress="progress"
					class="button" icon="page-next-16-full">
					{{msg('install.butt.next')}}
				</progress-button>
			</td>
		</tr>
	</table>
	</form>
	</div>
</template>

